import { useCartStore, type CartItem as CartItemType } from '../../hooks';

interface CartItemProps {
  item: CartItemType;
}

export function CartItem({ item }: CartItemProps) {
  const { incrementQuantity, decrementQuantity, removeItem } = useCartStore();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const subtotal = item.price * item.quantity;

  return (
    <div className="flex gap-3 py-3 border-b border-gray-100 last:border-b-0">
      <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
        {item.imageUrl ? (
          <span className="text-2xl">{item.imageUrl}</span>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
            <span className="text-primary-400 font-medium">
              {item.productName.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <h4 className="font-medium text-gray-900 text-sm truncate">{item.productName}</h4>
          <button
            onClick={() => removeItem(item.productId)}
            className="text-gray-400 hover:text-red-500 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-500">{formatPrice(item.price)}</p>
        {item.variantName && (
          <p className="text-xs text-primary-600 font-medium">Varian: {item.variantName}</p>
        )}

        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => decrementQuantity(item.productId)}
              className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="w-6 text-center font-medium text-sm">{item.quantity}</span>
            <button
              onClick={() => incrementQuantity(item.productId)}
              disabled={item.quantity >= item.stock}
              className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          <p className="font-semibold text-primary-600 text-sm">{formatPrice(subtotal)}</p>
        </div>

        {item.notes && (
          <p className="text-xs text-gray-400 mt-1 truncate">Catatan: {item.notes}</p>
        )}
      </div>
    </div>
  );
}
